import React, { useState, useMemo, useRef } from 'react';
import { TagFilter, VideoTag } from '@models/section/videoLibrarySection';
import { Heading, LazyLoadContainer, Pagination, Text } from '@web-for-marketing/react-ui';
import { Grid, Skeleton, Stack } from '@mui/material';
import { videoLibraryStyles as classes } from './videoLibraryStyles';
import { TagFilterBar } from './TagFilterBar';
import { MobileTagFilterBar } from './MobileTagFilterBar';
import { VideoLibraryCard } from './VideoLibraryCard';
import { isMobileAtom } from '@atoms/appSettings';
import { useAtom } from 'jotai';
import { useVideosByTags } from '@queries/publicVideo';

interface VideoLibraryContentProps {
    lazyLoad: boolean;
    title: string | React.ReactElement;
    description: string;
    tags: VideoTag[];
    displayTagCategories: boolean;
    connect2024?: boolean;
    headerSection?: boolean;
    scrollTopSpacing?: number;
}

export function VideoLibraryContent({
    lazyLoad,
    tags,
    title,
    description,
    displayTagCategories,
    connect2024,
    headerSection,
    scrollTopSpacing = 0,
}: VideoLibraryContentProps): React.ReactElement {
    const containerRef = useRef<null | HTMLDivElement>(null);
    const [selectedTag, setSelectedTag] = useState<TagFilter>('All');
    const [isMobile] = useAtom(isMobileAtom);
    const [page, setPage] = useState(1);
    const prevTagsLength = useRef(tags.length);

    // Reset selected tag and page when tags change from admin form
    if (prevTagsLength.current !== tags.length) {
        prevTagsLength.current = tags.length;
        setSelectedTag('All');
        setPage(1);
    }

    const filteredTagIds = useMemo(() => {
        if (selectedTag !== 'All') {
            return [selectedTag];
        } else {
            const allTagIds = tags.map((tag) => tag.id);
            return allTagIds;
        }
    }, [selectedTag, tags]);

    const { data: videos, isPending } = useVideosByTags(filteredTagIds, page);

    function handlePageChange(page: number): void {
        if (page !== videos?.currentPage) {
            setPage(page);
            if (containerRef.current) {
                const { top } = containerRef.current.getBoundingClientRect();
                const scrollPosition = window.scrollY + top - (isMobile ? 0 : scrollTopSpacing);
                window.scrollTo({ top: scrollPosition });
            }
        }
    }

    function handleTagChange(tag: TagFilter): void {
        setSelectedTag(tag);
        setPage(1);
    }

    return (
        <div>
            {title ? (
                <Heading
                    align='center'
                    variant='h2'
                    component={headerSection ? 'h1' : 'h2'}
                    color={connect2024 ? 'inversePrimary' : undefined}
                >
                    {title}
                </Heading>
            ) : null}
            {description ? (
                <Text align='center' css={{ marginTop: '2rem' }} color={connect2024 ? 'inversePrimary' : undefined}>
                    {description}
                </Text>
            ) : null}
            <Grid container direction='column' alignItems='center' css={{ maxWidth: '135rem' }} ref={containerRef}>
                {displayTagCategories && tags.length ? (
                    <div css={[classes.topSpacing, { width: '100%' }]}>
                        {isMobile ? (
                            <MobileTagFilterBar
                                onChange={handleTagChange}
                                selectedTag={selectedTag}
                                tags={tags}
                                connect2024={connect2024}
                            />
                        ) : (
                            <TagFilterBar
                                connect2024={connect2024}
                                onChange={handleTagChange}
                                selectedTag={selectedTag}
                                tags={tags}
                            />
                        )}
                    </div>
                ) : null}
                {isPending ? (
                    <div css={classes.topSpacing} data-testid='videoLibrarySkeletons'>
                        <Grid container spacing={8}>
                            {Array.from(new Array(6)).map((_item, index) => (
                                <Grid
                                    key={index}
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                        md: 4,
                                    }}>
                                    <div css={classes.skeletonVideoCard(connect2024)}>
                                        <Skeleton variant='rounded' animation='wave' height={250} />
                                        <div css={classes.cardText}>
                                            <Skeleton variant='rounded' animation='wave' height={30} width={100} />
                                            <Skeleton variant='rectangular' animation='wave' height={60} />
                                            <Skeleton variant='rectangular' animation='wave' height={20} width={80} />
                                        </div>
                                    </div>
                                </Grid>
                            ))}
                        </Grid>
                    </div>
                ) : videos ? (
                    <>
                        <div css={[classes.topSpacing, { width: '100%' }]}>
                            <LazyLoadContainer component={Grid} lazyload={lazyLoad} container spacing={8}>
                                {videos.pageRecords.map((video) => {
                                    return (
                                        <Grid
                                            key={video.id}
                                            size={{
                                                xs: 12,
                                                sm: 6,
                                                md: 4,
                                            }}>
                                            <VideoLibraryCard
                                                connect2024={connect2024}
                                                video={video}
                                                lazyLoad={lazyLoad}
                                            />
                                        </Grid>
                                    );
                                })}
                            </LazyLoadContainer>
                        </div>
                        {videos.totalRecordCount > videos.maxRecordsPerPage ? (
                            <Stack alignItems='center' css={classes.topSpacing}>
                                <Pagination
                                    currentPage={videos.currentPage}
                                    totalRecordCount={videos.totalRecordCount}
                                    maxRecordsPerPage={videos.maxRecordsPerPage}
                                    onPageChange={handlePageChange}
                                    color={connect2024 ? 'white' : 'blue'}
                                />
                            </Stack>
                        ) : null}
                    </>
                ) : null}
            </Grid>
        </div>
    );
}
